import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { RegistrationService } from '../../registration.service';
import { GlobalSearchStakeholderComponent } from '../../../../shared/components/global-search-stakeholder/global-search-stakeholder.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RegisterForMeetingRequestDTO } from '../../types/RegisterForMeetingRequestDTO';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { PublicMeetingDTO } from '../../types/PublicMeetingDTO';
import { ActivatedRoute } from '@angular/router';
import { GTranslateWidgetService } from 'src/app/shared/services/gtranslate-widget.service';

@Component({
  selector: 'app-in-person-registration',
  imports: [
    CommonModule,
    GlobalSearchStakeholderComponent,
    MatCheckbox,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatLabel,
  ],
  templateUrl: './in-person-registration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './in-person-registration.component.scss',
})
export class InPersonRegistrationComponent implements OnInit, OnDestroy {
  private subscription: Subscription | null = null;
  meetingInfo: PublicMeetingDTO | null = null;
  isStakeholderSearchValid: boolean = false;
  registrationForm: FormGroup;
  onStakeholderSearch: number = 0;

  constructor(
    private registrationService: RegistrationService,
    private urlParamsService: UrlParamsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private gTranslateService: GTranslateWidgetService
  ) {
    this.registrationForm = this.fb.group({
      StakeGuid: ['', Validators.required],
      MeetingGuid: ['', Validators.required],
      IsOnline: [false, Validators.required],
      TotalAttendees: [1, Validators.required],
      IsAnonymous: [false],
      IsMultipleAttending: [false],
      ExtraAttendees: [0],
    });
  }

  ngOnInit(): void {
    this.subscription = this.registrationService.meetingInfo$.subscribe({
      next: (meetingInfo) => {
        this.meetingInfo = meetingInfo;
        this.registrationForm.get('MeetingGuid')?.setValue(meetingInfo?.MeetingGuid);
      },
      error: (error) => console.error('Error fetching meeting info:', error),
    });

    this.registrationForm.get('IsMultipleAttending')?.valueChanges.subscribe((isMultiple) => {
      if (!isMultiple) {
        this.registrationForm.get('ExtraAttendees')?.setValue(0);
      } else {
        setTimeout(() => this.gTranslateService.retranslate());
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  handleStakeholderSearchResult(event: { status: string; data: any; formValues: any }): void {
    if (event.status === 'success') {
      if (event.data?.StakeGuid) {
        this.registerForMeeting({
          StakeGuid: event.data.StakeGuid,
          MeetingGuid: this.registrationForm.get('MeetingGuid')?.value,
          IsOnline: this.registrationForm.get('IsOnline')?.value,
          TotalAttendees: this.calculateTotalAttendees(),
          IsAnonymous: this.registrationForm.get('IsAnonymous')?.value,
        });
      } else {
        this.urlParamsService.navigateWithParams('/registration/new-stakeholder', {
          LastName: event.formValues.LastName,
          Mail1ZipCode: event.formValues.Mail1ZipCode,
          StakeEmail: event.formValues.StakeEmail,
          SubscribeSourceId: 3,
          isEdit: false,
          backUrl: '/registration/in-person-registration',
          registrationMode: 'in-person',
        });
      }
    } else if (event.status === 'error') {
      console.error('Error fetching stakeholder:', event.data);
      this.urlParamsService.navigateToErrorPage('Error searching for stakeholder.');
    }
  }

  handleFormValidity(event: boolean): void {
    this.isStakeholderSearchValid = event;
  }

  async onRegisterForMeeting(): Promise<void> {
    // Emits a value to the stake search component to run the search API call
    this.onStakeholderSearch++;
  }

  isSubmitDisabled(): boolean {
    return !this.registrationForm.get('IsAnonymous')?.value && !this.isStakeholderSearchValid;
  }

  private registerForMeeting(registrant: RegisterForMeetingRequestDTO): void {
    this.registrationService.registerStakeholderForMeeting(registrant).subscribe({
      next: () => {
        this.urlParamsService.navigateWithParams('/registration/in-person/confirmation');
      },
      error: (error) => {
        console.error('Error registering stakeholder for meeting:', error);
        this.onStakeholderSearch = 0;
        this.urlParamsService.navigateToErrorPage(
          'An error occurred while registering for the meeting.'
        );
      },
    });
  }

  private calculateTotalAttendees(): number {
    return (
      (this.registrationForm.get('TotalAttendees')?.value || 1) +
      (this.registrationForm.get('ExtraAttendees')?.value || 0)
    );
  }
}
